import { Button } from "../Button/index.js";
import { useCallback, useMemo } from "react";
import { IconArrowLeft, IconArrowRight } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/Pagination/index.tsx
const DOTS = "...";
const filledArray = (length) => Array.from({ length }, (_, index) => index + 1);
/**
* Компонент Pagination
* @param totalItems — общее количество элементов
* @param itemsPerPage — количество элементов на странице
* @param currentPage — текущая страница
* @param setCurrentPage — функция установки текущей страницы
* @param size — размер кнопок (xs, sm, md, lg)
* @param inactiveTheme — тема неактивных кнопок (по умолчанию secondary)
*/
const Pagination = ({ itemsPerPage, totalItems, setCurrentPage, size = "md", inactiveTheme = "secondary", currentPage, className }) => {
	const pageNumbers = useMemo(() => filledArray(Math.ceil(totalItems / itemsPerPage)), [totalItems, itemsPerPage]);
	const totalPages = pageNumbers.length;
	const getPagination = useCallback(() => {
		switch (true) {
			case pageNumbers.length <= 5: return pageNumbers;
			case currentPage >= 1 && currentPage <= 3: return [
				1,
				2,
				3,
				4,
				DOTS,
				pageNumbers.length
			];
			case currentPage === 4: return [
				...pageNumbers.slice(0, 5),
				DOTS,
				pageNumbers.length
			];
			case currentPage > 4 && currentPage < pageNumbers.length - 2: {
				const sliced1 = pageNumbers.slice(currentPage - 2, currentPage);
				const sliced2 = pageNumbers.slice(currentPage, currentPage + 1);
				return [
					1,
					DOTS,
					...sliced1,
					...sliced2,
					DOTS,
					pageNumbers.length
				];
			}
			case currentPage > pageNumbers.length - 3: {
				const sliced = pageNumbers.slice(pageNumbers.length - 4);
				return [
					1,
					DOTS,
					...sliced
				];
			}
			default: return [];
		}
	}, [pageNumbers, currentPage]);
	const pagination = useMemo(() => getPagination(), [getPagination]);
	const handlePrevPage = () => {
		setCurrentPage(currentPage - 1);
	};
	const handleNextPage = () => {
		setCurrentPage(currentPage + 1);
	};
	const renderPageNumbers = pagination.map((pageNumber, index) => {
		const isCurrentPage = pageNumber === currentPage;
		if (pageNumber === DOTS) return /* @__PURE__ */ jsx("div", {
			className: cn("text-type-minor flex items-center justify-center p-0", size === "sm" ? "size-8" : "size-6"),
			children: pageNumber
		}, `dots-${index}`);
		return /* @__PURE__ */ jsx(Button, {
			"data-cy": "pagination-button-active",
			onClick: () => setCurrentPage(pageNumber),
			size,
			shape: "square",
			theme: isCurrentPage ? "primary" : inactiveTheme,
			children: pageNumber
		}, pageNumber);
	});
	if (totalPages === 0) return null;
	return /* @__PURE__ */ jsxs("div", {
		"data-cy": "pagination-div",
		className: cn("flex justify-center gap-1 pb-12", className),
		children: [
			/* @__PURE__ */ jsx(Button, {
				icon: /* @__PURE__ */ jsx(IconArrowLeft, {
					"data-cy": "pagination-iconArrowLeft",
					className: "size-4"
				}),
				size,
				onClick: handlePrevPage,
				disabled: currentPage === 1,
				shape: "square",
				theme: inactiveTheme
			}),
			renderPageNumbers,
			/* @__PURE__ */ jsx(Button, {
				icon: /* @__PURE__ */ jsx(IconArrowRight, {
					"data-cy": "pagination-iconArrowRight",
					className: "size-4"
				}),
				size,
				onClick: handleNextPage,
				disabled: currentPage === totalPages,
				shape: "square",
				theme: inactiveTheme
			})
		]
	});
};
//#endregion
export { Pagination };

//# sourceMappingURL=index.js.map